import type { Template, TemplateLayer } from "../types";
import {
  applyBlackAndWhite,
  applyBrightnessContrast,
  applyColorBalance,
  applyGradientMap,
  applyHueSaturation,
  applyPhotoFilter,
  applySelectiveColor,
  applyVibrance,
} from "./adjustments";
import { canvasToWebpBlobCapped } from "./webp";

const BLEND_MAP: Record<string, GlobalCompositeOperation> = {
  normal: "source-over",
  multiply: "multiply",
  screen: "screen",
  overlay: "overlay",
  darken: "darken",
  lighten: "lighten",
  "color dodge": "color-dodge",
  "color burn": "color-burn",
  "hard light": "hard-light",
  "soft light": "soft-light",
  difference: "difference",
  exclusion: "exclusion",
  hue: "hue",
  saturation: "saturation",
  color: "color",
  luminosity: "luminosity",
};

function mapBlend(blend?: string): GlobalCompositeOperation {
  if (!blend) return "source-over";
  return BLEND_MAP[blend] ?? "source-over";
}

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Region an adjustment layer applies to: its clip rect, or the whole template. */
function adjustRegion(layer: TemplateLayer, template: Template): Rect {
  if (
    layer.clipX != null &&
    layer.clipY != null &&
    layer.clipWidth != null &&
    layer.clipHeight != null
  ) {
    return { x: layer.clipX, y: layer.clipY, width: layer.clipWidth, height: layer.clipHeight };
  }
  return { x: 0, y: 0, width: template.width, height: template.height };
}

function drawCover(
  ctx: CanvasRenderingContext2D,
  src: HTMLImageElement | HTMLCanvasElement,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
) {
  const srcW = src.width;
  const srcH = src.height;
  if (!srcW || !srcH) return;
  const srcRatio = srcW / srcH;
  const dstRatio = dw / dh;
  let sx = 0;
  let sy = 0;
  let sw = srcW;
  let sh = srcH;
  if (srcRatio > dstRatio) {
    sw = srcH * dstRatio;
    sx = (srcW - sw) / 2;
  } else {
    sh = srcW / dstRatio;
    sy = (srcH - sh) / 2;
  }
  ctx.drawImage(src, sx, sy, sw, sh, dx, dy, dw, dh);
}

export interface RenderOptions {
  template: Template;
  canvases: Map<string, HTMLCanvasElement>;
  slotOverrides?: Map<string, HTMLImageElement>;
  /** Draw dashed outlines + label for slots without overrides */
  drawSlotPlaceholders?: boolean;
  highlightedLayerZ?: number | null;
}

export function renderTemplate(canvas: HTMLCanvasElement, opts: RenderOptions): void {
  const { template, canvases, slotOverrides, drawSlotPlaceholders, highlightedLayerZ } = opts;
  canvas.width = template.width;
  canvas.height = template.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  for (const layer of template.layers) {
    ctx.save();
    ctx.globalAlpha = layer.opacity ?? 1;
    ctx.globalCompositeOperation = mapBlend(layer.blendMode);

    if (layer.mask && layer.mask.defaultColor === 0 && layer.mask.width > 0 && layer.mask.height > 0) {
      ctx.beginPath();
      ctx.rect(layer.mask.left, layer.mask.top, layer.mask.width, layer.mask.height);
      ctx.clip();
    }

    if (layer.type === "image") {
      const src = canvases.get(layer.imageUrl);
      if (src && layer.width > 0 && layer.height > 0) {
        ctx.drawImage(src, layer.x, layer.y, layer.width, layer.height);
      }
    } else if (layer.type === "slot") {
      const override = slotOverrides?.get(layer.id);
      if (override && layer.width > 0 && layer.height > 0) {
        drawCover(ctx, override, layer.x, layer.y, layer.width, layer.height);
      }
    } else if (layer.type === "colorFill" && layer.width > 0 && layer.height > 0) {
      ctx.fillStyle = layer.color;
      ctx.fillRect(layer.x, layer.y, layer.width, layer.height);
    } else if (layer.type === "photoFilter") {
      ctx.restore();
      applyPhotoFilter(ctx, adjustRegion(layer, template), layer.color, layer.density, layer.preserveLuminosity);
      continue;
    } else if (layer.type === "blackAndWhite") {
      ctx.restore();
      applyBlackAndWhite(ctx, adjustRegion(layer, template), layer.weights, layer.tintColor);
      continue;
    } else if (layer.type === "brightnessContrast") {
      ctx.restore();
      applyBrightnessContrast(ctx, adjustRegion(layer, template), layer.brightness, layer.contrast);
      continue;
    } else if (layer.type === "hueSaturation") {
      ctx.restore();
      applyHueSaturation(ctx, adjustRegion(layer, template), layer.hue, layer.saturation, layer.lightness);
      continue;
    } else if (layer.type === "vibrance") {
      ctx.restore();
      applyVibrance(ctx, adjustRegion(layer, template), layer.vibrance, layer.saturation);
      continue;
    } else if (layer.type === "colorBalance") {
      ctx.restore();
      applyColorBalance(
        ctx,
        adjustRegion(layer, template),
        layer.shadows,
        layer.midtones,
        layer.highlights,
        layer.preserveLuminosity,
      );
      continue;
    } else if (layer.type === "selectiveColor") {
      ctx.restore();
      applySelectiveColor(ctx, adjustRegion(layer, template), layer.mode, {
        reds: layer.reds,
        yellows: layer.yellows,
        greens: layer.greens,
        cyans: layer.cyans,
        blues: layer.blues,
        magentas: layer.magentas,
        whites: layer.whites,
        neutrals: layer.neutrals,
        blacks: layer.blacks,
      });
      continue;
    } else if (layer.type === "gradientMap") {
      ctx.restore();
      applyGradientMap(ctx, adjustRegion(layer, template), layer.stops, layer.reverse ?? false);
      continue;
    }

    ctx.restore();
  }

  if (drawSlotPlaceholders) {
    ctx.save();
    ctx.globalCompositeOperation = "source-over";
    ctx.globalAlpha = 1;
    for (const layer of template.layers) {
      if (layer.type !== "slot") continue;
      if (layer.width <= 0 || layer.height <= 0) continue;
      const isHighlighted = highlightedLayerZ === layer.z;
      ctx.strokeStyle = isHighlighted ? "#facc15" : "rgba(99,102,241,0.95)";
      ctx.lineWidth = isHighlighted ? 8 : 6;
      ctx.setLineDash([14, 10]);
      ctx.strokeRect(layer.x, layer.y, layer.width, layer.height);
      ctx.setLineDash([]);
      ctx.fillStyle = "rgba(99,102,241,0.95)";
      ctx.font = "bold 28px ui-sans-serif, system-ui, sans-serif";
      const padding = 8;
      const metrics = ctx.measureText(layer.id);
      const tw = metrics.width + padding * 2;
      const th = 36;
      ctx.fillRect(layer.x, layer.y, tw, th);
      ctx.fillStyle = "#ffffff";
      ctx.textBaseline = "middle";
      ctx.fillText(layer.id, layer.x + padding, layer.y + th / 2);
    }
    ctx.restore();
  }
}

export async function capturePreview(
  template: Template,
  canvases: Map<string, HTMLCanvasElement>,
  slotOverrides: Map<string, HTMLImageElement>,
  maxDim = 1080,
  quality = 0.85,
): Promise<Blob> {
  const off = document.createElement("canvas");
  renderTemplate(off, { template, canvases, slotOverrides, drawSlotPlaceholders: false });

  const scale = Math.min(1, maxDim / Math.max(off.width, off.height));
  const target = scale < 1 ? document.createElement("canvas") : off;
  if (scale < 1) {
    target.width = Math.round(off.width * scale);
    target.height = Math.round(off.height * scale);
    const tctx = target.getContext("2d");
    if (tctx) {
      tctx.imageSmoothingEnabled = true;
      tctx.imageSmoothingQuality = "high";
      tctx.drawImage(off, 0, 0, target.width, target.height);
    }
  }

  return canvasToWebpBlobCapped(target, undefined, quality);
}
